import { Project } from '../types/project';
import { citationsFor } from './calc/citations';

export interface DataQualityIssue {
  type: 'error' | 'warning' | 'info';
  category: 'nec' | 'gap' | 'anomaly' | 'general';
  message: string;
}

export type DataQualityTier = 'green' | 'yellow' | 'red';

export interface DataQualityResult {
  tier: DataQualityTier;
  issues: DataQualityIssue[];
}

interface DataPoint {
  timestamp: string;
  kw: number;
}

/**
 * A dataset counts as a full year at 364 days of span, first reading to last.
 * Span undercounts by design: a 12-month export's first reading closes the
 * first interval and its last one opens no new one, so a whole year measures
 * 364.99 days, and a utility file that stops an hour or a day short of its
 * end date is still that year. Missing data inside the span is the gap
 * check's job, not this one's. Matches PARTIAL_YEAR_MAX_DAYS in
 * ami/plausibilityValidator.ts. Before 2026-10-08 the cut was 365, which
 * told a real year (BED, 364.96 days) it could be used only under the
 * 30-day Exception, on the printed report.
 */
export const FULL_YEAR_MIN_DAYS = 364;

const DAYS_PER_MONTH = 365.25 / 12;

// Peak season months: Dec(12), Jan(1), Feb(2), Jun(6), Jul(7), Aug(8)
const PEAK_SEASON_MONTHS = new Set([12, 1, 2, 6, 7, 8]);

export function analyzeDataQuality(
  amiData: DataPoint[],
  project: Partial<Project>
): DataQualityResult {
  if (amiData.length === 0) {
    return { tier: 'green', issues: [] };
  }

  const sortedData = [...amiData].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const necIssues = checkNEC22087Requirements(sortedData, project);
  const gapIssues = checkDataGaps(
    sortedData,
    project.ami_interval_minutes,
    citationsFor(project).measured
  );
  const anomalyIssues = checkAnomalies(sortedData);
  const flatLineIssues = checkFlatLineRuns(sortedData);

  const issues = [...necIssues, ...gapIssues, ...anomalyIssues, ...flatLineIssues];
  const tier = determineTier(sortedData);

  return { tier, issues };
}

function determineTier(sortedData: DataPoint[]): DataQualityTier {
  const firstDate = new Date(sortedData[0].timestamp);
  const lastDate = new Date(sortedData[sortedData.length - 1].timestamp);
  const daysCovered = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);

  // Missing days
  const uniqueDates = new Set(sortedData.map(d => new Date(d.timestamp).toDateString()));
  const expectedDays = Math.ceil(daysCovered) + 1;
  const missingDays = Math.max(0, expectedDays - uniqueDates.size);
  const missingDaysPct = expectedDays > 0 ? (missingDays / expectedDays) * 100 : 0;

  // Gaps > 72 hours — categorized by peak season
  let hasRedGap = false;
  let hasYellowGap = false;
  const seventyTwoHoursMs = 72 * 60 * 60 * 1000;
  for (let i = 1; i < sortedData.length; i++) {
    const gapMs =
      new Date(sortedData[i].timestamp).getTime() -
      new Date(sortedData[i - 1].timestamp).getTime();
    if (gapMs > seventyTwoHoursMs) {
      const gapMonth = new Date(sortedData[i].timestamp).getUTCMonth() + 1;
      if (PEAK_SEASON_MONTHS.has(gapMonth)) {
        hasRedGap = true;
      } else {
        hasYellowGap = true;
      }
    }
  }

  // Flat-line run length
  const maxRun = computeMaxFlatLineRun(sortedData);

  // Duplicate timestamp rate
  const timestamps = sortedData.map(d => d.timestamp);
  const duplicateCount = timestamps.length - new Set(timestamps).size;
  const duplicateRate = timestamps.length > 0 ? (duplicateCount / timestamps.length) * 100 : 0;

  // Season coverage
  const seasonsCovered = determineSeasonsCovered(sortedData);

  // RED: any one triggers
  if (
    daysCovered < 30 ||
    missingDaysPct > 10 ||
    hasRedGap ||
    maxRun >= 600
  ) {
    return 'red';
  }

  // YELLOW: any one triggers (no red present)
  if (
    daysCovered < 335 ||
    missingDaysPct >= 1 ||
    hasYellowGap ||
    maxRun >= 200 ||
    duplicateRate > 2 ||
    seasonsCovered.size < 2
  ) {
    return 'yellow';
  }

  return 'green';
}

// Returns the length of the longest run of consecutive identical non-zero kW readings.
function computeMaxFlatLineRun(sortedData: DataPoint[]): number {
  if (sortedData.length === 0) return 0;
  let maxRun = 1;
  let runLength = 1;
  let currentValue = sortedData[0].kw;
  for (let i = 1; i < sortedData.length; i++) {
    if (sortedData[i].kw !== 0 && sortedData[i].kw === currentValue) {
      runLength++;
      if (runLength > maxRun) maxRun = runLength;
    } else {
      currentValue = sortedData[i].kw;
      runLength = 1;
    }
  }
  return maxRun;
}

function checkFlatLineRuns(sortedData: DataPoint[]): DataQualityIssue[] {
  const issues: DataQualityIssue[] = [];
  const maxRun = computeMaxFlatLineRun(sortedData);

  if (maxRun >= 600) {
    issues.push({
      type: 'error',
      category: 'anomaly',
      message: `Flat-line detected: ${maxRun} consecutive readings with identical non-zero values. Data may be frozen or incorrectly recorded.`,
    });
  } else if (maxRun >= 200) {
    issues.push({
      type: 'warning',
      category: 'anomaly',
      message: `${maxRun} consecutive readings with identical non-zero values detected. Verify the data is not frozen or replicated.`,
    });
  }

  return issues;
}

function checkNEC22087Requirements(
  sortedData: DataPoint[],
  project: Partial<Project>
): DataQualityIssue[] {
  const issues: DataQualityIssue[] = [];
  // These messages become `ami_data_quality_notes`, which prints on the
  // report — so the section number follows the project's code edition rather
  // than naming Article 220 to a jurisdiction on Article 120.
  const citations = citationsFor(project);
  const cite = citations.measured;
  // The renewable / peak-load-shaving bar on the Exception exists from 2020
  // on; the 2017 Exception (Chicago) has none, so the note must not invent one.
  const exceptionBar = citations.pack.exceptionBarsRenewables
    ? ', and is not permitted where the service has a renewable energy system (solar photovoltaic or wind) or any form of peak load shaving'
    : '';

  if (sortedData.length === 0) return issues;

  const firstDate = new Date(sortedData[0].timestamp);
  const lastDate = new Date(sortedData[sortedData.length - 1].timestamp);

  const daysCovered =
    (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);

  if (daysCovered < 30) {
    issues.push({
      type: 'error',
      category: 'nec',
      message: `NEC ${cite} is based on 1 year of maximum demand data; without it, the section's Exception requires a continuous recording of at least 30 days. Current data: ${Math.round(daysCovered)} days.`,
    });
  } else if (daysCovered >= 30 && daysCovered < 90) {
    issues.push({
      type: 'info',
      category: 'nec',
      message: `Data covers ${Math.round(daysCovered)} days, less than the 1-year period NEC ${cite} is based on. It can be used only under the section's Exception, which requires a continuous recording of at least 30 days taken while the building is occupied, must include the larger of the heating or cooling load by measurement or calculation${exceptionBar}.`,
    });
  } else if (daysCovered >= 90 && daysCovered < FULL_YEAR_MIN_DAYS) {
    issues.push({
      type: 'info',
      category: 'nec',
      message: `Data covers ${Math.round(daysCovered)} days (${(daysCovered / DAYS_PER_MONTH).toFixed(1)} months), less than the 1-year period NEC ${cite} is based on. It can be used only under the section's Exception, which requires a continuous recording of at least 30 days taken while the building is occupied, must include the larger of the heating or cooling load by measurement or calculation${exceptionBar}.`,
    });
  } else {
    issues.push({
      type: 'info',
      category: 'nec',
      message: `Data covers ${Math.round(daysCovered)} days (${(daysCovered / 365).toFixed(1)} years), providing comprehensive analysis across all seasons per NEC ${cite}.`,
    });
  }

  if (project.installation_status !== 'existing') {
    issues.push({
      type: 'error',
      category: 'nec',
      message: `NEC ${cite} applies only to existing installations. Installation status must be "existing".`,
    });
  }

  const seasonsCovered = determineSeasonsCovered(sortedData);
  if (seasonsCovered.size === 4) {
    issues.push({
      type: 'info',
      category: 'nec',
      message: 'Data covers all four seasons, providing comprehensive peak analysis.',
    });
  } else if (seasonsCovered.size >= 2) {
    const seasons = Array.from(seasonsCovered).join(', ');
    issues.push({
      type: 'warning',
      category: 'nec',
      message: `Data covers ${seasonsCovered.size} seasons (${seasons}). Full year data recommended for complete seasonal analysis.`,
    });
  } else {
    const season = Array.from(seasonsCovered)[0];
    issues.push({
      type: 'warning',
      category: 'nec',
      message: `Data covers only ${season}. Peak demand may be different in other seasons.`,
    });
  }

  return issues;
}

function checkDataGaps(
  sortedData: DataPoint[],
  expectedInterval?: number | null,
  cite: string = '220.87'
): DataQualityIssue[] {
  const issues: DataQualityIssue[] = [];

  if (sortedData.length < 2) return issues;

  const intervals: number[] = [];
  for (let i = 1; i < sortedData.length; i++) {
    const diff =
      (new Date(sortedData[i].timestamp).getTime() -
        new Date(sortedData[i - 1].timestamp).getTime()) /
      (1000 * 60);
    intervals.push(diff);
  }

  const avgInterval =
    intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
  const threshold = (expectedInterval || avgInterval) * 2;

  const gaps: Array<{ index: number; minutes: number; timestamp: string }> = [];
  for (let i = 0; i < intervals.length; i++) {
    if (intervals[i] > threshold) {
      gaps.push({
        index: i + 1,
        minutes: intervals[i],
        timestamp: sortedData[i + 1].timestamp,
      });
    }
  }

  if (gaps.length === 0) {
    issues.push({
      type: 'info',
      category: 'gap',
      message: 'No significant data gaps detected. Continuous monitoring confirmed.',
    });
  } else if (gaps.length <= 3) {
    gaps.forEach((gap) => {
      const hours = (gap.minutes / 60).toFixed(1);
      const date = new Date(gap.timestamp).toLocaleDateString(undefined, { timeZone: 'UTC' });
      issues.push({
        type: 'warning',
        category: 'gap',
        message: `Data gap of ${hours} hours detected at ${date}. Peak may have been missed during this period.`,
      });
    });
  } else {
    const totalGapMinutes = gaps.reduce((sum, gap) => sum + gap.minutes, 0);
    const totalGapHours = (totalGapMinutes / 60).toFixed(1);
    issues.push({
      type: 'warning',
      category: 'gap',
      message: `${gaps.length} data gaps detected totaling ${totalGapHours} hours. Peak demand accuracy may be affected.`,
    });
  }

  const uniqueDates = new Set(
    sortedData.map((d) => new Date(d.timestamp).toDateString())
  );
  const firstDate = new Date(sortedData[0].timestamp);
  const lastDate = new Date(sortedData[sortedData.length - 1].timestamp);
  const expectedDays =
    Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const missingDays = expectedDays - uniqueDates.size;

  if (missingDays > 0) {
    const percentMissing = ((missingDays / expectedDays) * 100).toFixed(1);
    if (missingDays > 7) {
      issues.push({
        type: 'error',
        category: 'gap',
        message: `${missingDays} days of data missing (${percentMissing}% of measurement period). Data may not meet NEC ${cite} continuous monitoring requirement.`,
      });
    } else {
      issues.push({
        type: 'warning',
        category: 'gap',
        message: `${missingDays} days have incomplete data (${percentMissing}% of measurement period).`,
      });
    }
  }

  return issues;
}

function checkAnomalies(sortedData: DataPoint[]): DataQualityIssue[] {
  const issues: DataQualityIssue[] = [];

  if (sortedData.length < 10) return issues;

  const values = sortedData.map((d) => d.kw);
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance =
    values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

  const zeroCount = values.filter((v) => v === 0).length;
  const zeroPercent = (zeroCount / values.length) * 100;

  if (zeroPercent > 5) {
    issues.push({
      type: 'warning',
      category: 'anomaly',
      message: `${zeroPercent.toFixed(1)}% of readings are zero. Verify if this represents actual usage or missing data.`,
    });
  }

  // Negative values are filtered in fileParser before reaching here; this check will not fire.
  // Disclosure of excluded negatives is handled in the parser's dataQuality notes instead.

  const outliers = values.filter((v) => Math.abs(v - mean) > 3 * stdDev);
  if (outliers.length > 0 && outliers.length < values.length * 0.01) {
    const maxOutlier = Math.max(...outliers);
    const outliersCount = outliers.length;
    issues.push({
      type: 'info',
      category: 'anomaly',
      // No kilowatt value here: this line shows on the Existing Load tab before
      // the project is unlocked, and the measured peak is paid content. The
      // peak itself prints on the report and the Results tab.
      message: `${outliersCount} statistical outlier(s) detected, ${(maxOutlier / mean).toFixed(0)}× the average reading. These may represent legitimate peak events or measurement errors.`,
    });
  }

  const max = Math.max(...values);
  const min = Math.min(...values.filter((v) => v > 0));

  if (min > 0 && max / min > 100) {
    issues.push({
      type: 'info',
      category: 'anomaly',
      // Same rule: a ratio, not the maximum in kW.
      message: `Large variation between minimum and maximum demand (${(max / min).toFixed(0)}×). Verify data represents typical occupancy patterns.`,
    });
  }

  // Compares the last ~1,000 readings (about ten days at 15 minutes) with
  // the whole dataset: on a short recording, a recent drop can mean the
  // house emptied. Across a full year the last ten days are one season
  // against the year's peak, so nearly every annual file tripped it and the
  // report told the AHJ to doubt the occupancy for no reason. Short data only.
  const spanDays =
    (new Date(sortedData[sortedData.length - 1].timestamp).getTime() -
      new Date(sortedData[0].timestamp).getTime()) /
    (1000 * 60 * 60 * 24);
  const recentPeak = sortedData
    .slice(-Math.min(1000, sortedData.length))
    .reduce((max, d) => (d.kw > max ? d.kw : max), 0);
  const overallPeak = Math.max(...values);

  if (spanDays < FULL_YEAR_MIN_DAYS && recentPeak < overallPeak * 0.7) {
    issues.push({
      type: 'warning',
      category: 'anomaly',
      message: `Peak demand occurs early in measurement period. Recent usage is ${((recentPeak / overallPeak) * 100).toFixed(0)}% of peak. Verify occupancy status during measurement.`,
    });
  }

  return issues;
}

function determineSeasonsCovered(sortedData: DataPoint[]): Set<string> {
  const seasons = new Set<string>();

  for (const point of sortedData) {
    const month = new Date(point.timestamp).getUTCMonth() + 1;
    if (month >= 12 || month <= 2) seasons.add('Winter');
    else if (month >= 3 && month <= 5) seasons.add('Spring');
    else if (month >= 6 && month <= 8) seasons.add('Summer');
    else seasons.add('Fall');
  }

  return seasons;
}

export function generateDataQualityNotes(issues: DataQualityIssue[]): string {
  if (issues.length === 0) {
    return 'No data quality issues detected.';
  }

  const sections: string[] = [];

  const errors = issues.filter((i) => i.type === 'error');
  if (errors.length > 0) {
    sections.push('ISSUES:\n' + errors.map((i) => `- ${i.message}`).join('\n'));
  }

  const warnings = issues.filter((i) => i.type === 'warning');
  if (warnings.length > 0) {
    sections.push('NOTES:\n' + warnings.map((i) => `- ${i.message}`).join('\n'));
  }

  const infos = issues.filter((i) => i.type === 'info');
  if (infos.length > 0) {
    sections.push('DATA SUMMARY:\n' + infos.map((i) => `- ${i.message}`).join('\n'));
  }

  return sections.join('\n\n');
}
